import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { ConectorWizard } from "@/components/conector/ConectorWizard";
import { PortalPage } from "@/components/portal/PortalPage";
import { PortalFiltersSheet } from "@/components/portal/PortalFiltersSheet";
import { PortalFiltersSheetProvider } from "@/contexts/portal-filters-sheet";
import { AuthDialog } from "@/components/site/AuthDialog";
import { supabase } from "@/integrations/supabase/client";
import { getConectorDemandaData } from "@/lib/conector.functions";


export const Route = createFileRoute("/conector/{-$id}")({
  validateSearch: (s: Record<string, unknown>) => ({
    editar: typeof s.editar === "string" ? s.editar : undefined,
  }),
  head: ({ params }) => ({
    meta: [
      {
        property: "og:url",
        content: params.id ? `/conector/${params.id}` : "/conector",
      },
    ],
    links: [
      {
        rel: "canonical",
        href: params.id ? `/conector/${params.id}` : "/conector",
      },
    ],
  }),
  component: ConectorRoute,
});

function ConectorRoute() {
  const { id } = Route.useParams();
  const { editar } = Route.useSearch();
  const forceEdit = editar === "1";

  // Si el id aparece DESPUÉS del mount inicial (el wizard acaba de crear la
  // demanda y sincronizó la URL), no volvemos a comprobar el estado ni
  // remontamos: sabemos que la demanda es nueva y el wizard sigue vivo.
  const hadIdOnMountRef = useRef(!!id);
  const initialIdRef = useRef<string | undefined>(id);

  const [estado, setEstado] = useState<string | null | undefined>(
    hadIdOnMountRef.current ? undefined : null,
  );
  const [referencia, setReferencia] = useState<string | null>(null);
  const [sessionResolved, setSessionResolved] = useState(false);
  const [isAuthed, setIsAuthed] = useState<boolean>(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setIsAuthed(!!data.session?.user);
      setSessionResolved(true);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_e, session) => {
      setIsAuthed(!!session?.user);
      setSessionResolved(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!hadIdOnMountRef.current) return;
    if (!id) return;
    if (id !== initialIdRef.current) return;
    if (!sessionResolved) return;
    if (!isAuthed) {
      setEstado(null);
      return;
    }
    getConectorDemandaData({ data: { id } })
      .then((dem) => {
        setEstado(dem?.estado ?? null);
        setReferencia(dem?.referencia ?? null);
      })
      .catch(() => setEstado(null));
  }, [id, isAuthed, sessionResolved]);

  // Fresh page load con /conector/$id → esperar a saber sesión / estado.
  if (hadIdOnMountRef.current && (!sessionResolved || (isAuthed && estado === undefined))) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // Fresh page load con /conector/$id y sin sesión → gate de login.
  // Tras autenticarse, el listener actualiza `isAuthed` y se carga la demanda.
  if (hadIdOnMountRef.current && sessionResolved && !isAuthed) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0B1120] px-4 py-10">
        <div className="w-full max-w-md rounded-2xl border border-border bg-card p-6 text-card-foreground shadow-2xl">
          <div className="mb-4 text-center">
            <h1 className="text-lg font-semibold">
              Inicia sesión para ver tus coincidencias
            </h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Accede con tu cuenta para ver el listado de locales que encajan con tu búsqueda.
            </p>
          </div>
          <AuthDialog inline />
        </div>
      </div>
    );
  }

  // Demanda ya iniciada → portal de resultados (solo en carga inicial con id).
  if (hadIdOnMountRef.current && estado === "iniciada" && !forceEdit && id) {
    return (
      <PortalFiltersSheetProvider>
        <PortalPage
          segments={[]}
          mode="demanda"
          demandaId={id}
          demandaRef={referencia}
          basePath={`/conector/${id}`}
        />
        <PortalFiltersSheet />
      </PortalFiltersSheetProvider>
    );
  }

  // Wizard: se mantiene montado aunque se añada el id a la URL.
  return <ConectorWizard initialDemandaId={initialIdRef.current} />;
}
